import config from '../../config';
import { getAuthHeaders, handleResponse } from './common';

const appendIfPresent = (params, key, value) => {
    if (value === undefined || value === null) return;
    const normalized = String(value).trim();
    if (normalized) params.append(key, normalized);
};

const buildQueryString = (filters = {}) => {
    const params = new URLSearchParams();
    appendIfPresent(params, 'fromDate', filters.fromDate);
    appendIfPresent(params, 'toDate', filters.toDate);
    appendIfPresent(params, 'clientReferenceId', filters.clientReferenceId);
    appendIfPresent(params, 'invoiceReferenceId', filters.invoiceReferenceId);
    appendIfPresent(params, 'status', filters.status);
    appendIfPresent(params, 'agingBucket', filters.agingBucket);
    appendIfPresent(params, 'paymentMethod', filters.paymentMethod);
    appendIfPresent(params, 'providerUserReferenceId', filters.providerUserReferenceId);
    appendIfPresent(params, 'appointmentType', filters.appointmentType);
    return params.toString();
};

const parseFilenameFromDisposition = (disposition) => {
    if (!disposition) return null;
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1]);
        } catch {
            return utf8Match[1];
        }
    }

    const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
    return filenameMatch?.[1] || null;
};

const readErrorMessage = async (response, fallback) => {
    const text = await response.text();
    if (!text) return fallback;

    try {
        const parsed = JSON.parse(text);
        return parsed?.error || parsed?.message || text || fallback;
    } catch {
        return text || fallback;
    }
};

const fetchReport = async (path, filters = {}) => {
    const query = buildQueryString(filters);
    const res = await fetch(`${config.api.url}/reports/${path}${query ? `?${query}` : ''}`, {
        headers: getAuthHeaders()
    });
    return handleResponse(res);
};

const fetchReportFile = async (path, filters = {}, format) => {
    const query = buildQueryString(filters);
    const finalQuery = `${query ? `${query}&` : ''}format=${encodeURIComponent(format)}`;
    const res = await fetch(`${config.api.url}/reports/${path}/export?${finalQuery}`, {
        headers: getAuthHeaders()
    });

    if (!res.ok) {
        const errorMessage = await readErrorMessage(res, `HTTP error! status: ${res.status}`);
        const error = new Error(errorMessage);
        error.status = res.status;
        error.statusText = res.statusText;
        throw error;
    }

    const blob = await res.blob();
    return {
        blob,
        filename: parseFilenameFromDisposition(res.headers.get('content-disposition')),
    };
};

const fetchBuilder = async (path, options = {}) => {
    const res = await fetch(`${config.api.url}/reports/builder/${path}`, {
        ...options,
        headers: getAuthHeaders(options.headers),
    });
    return handleResponse(res);
};

const fetchBuilderFile = async (definition, format, reportName) => {
    const params = new URLSearchParams({ format });
    if (reportName?.trim()) params.set('reportName', reportName.trim());
    const res = await fetch(`${config.api.url}/reports/builder/export?${params}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(definition),
    });
    if (!res.ok) {
        throw new Error(await readErrorMessage(res, `HTTP error! status: ${res.status}`));
    }
    return {
        blob: await res.blob(),
        filename: parseFilenameFromDisposition(res.headers.get('content-disposition')),
    };
};

const reportsApi = {
    getSummary: () => fetchReport('summary'),

    getAccountsReceivable: (filters = {}) => fetchReport('accounts-receivable', filters),
    exportAccountsReceivable: (filters = {}, format) => fetchReportFile('accounts-receivable', filters, format),

    getPayments: (filters = {}) => fetchReport('payments', filters),
    exportPayments: (filters = {}, format) => fetchReportFile('payments', filters, format),

    getClientInvoices: (filters = {}) => fetchReport('client-invoices', filters),
    exportClientInvoices: (filters = {}, format) => fetchReportFile('client-invoices', filters, format),

    getInvoiceAging: (filters = {}) => fetchReport('invoice-aging', filters),
    exportInvoiceAging: (filters = {}, format) => fetchReportFile('invoice-aging', filters, format),

    getClientAppointmentDetails: (filters = {}) => fetchReport('client-appointment-details', filters),
    exportClientAppointmentDetails: (filters = {}, format) => fetchReportFile('client-appointment-details', filters, format),

    getReportBuilderCatalog: () => fetchBuilder('catalog'),
    getSavedReports: () => fetchBuilder('saved'),
    createSavedReport: (payload) => fetchBuilder('saved', { method: 'POST', body: JSON.stringify(payload) }),
    updateSavedReport: (referenceId, payload) => fetchBuilder(`saved/${encodeURIComponent(referenceId)}`, { method: 'PUT', body: JSON.stringify(payload) }),
    deleteSavedReport: (referenceId) => fetchBuilder(`saved/${encodeURIComponent(referenceId)}`, { method: 'DELETE' }),
    previewDemographicReport: (definition) => fetchBuilder('preview', { method: 'POST', body: JSON.stringify(definition) }),
    exportDemographicReport: fetchBuilderFile,
};

export default reportsApi;
